import { ActionIcon, Flex, Menu, Text, Tooltip } from '@mantine/core';
import { IconFileDescription, IconFolderPlus, IconPlus } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import FieldSearch from '@/components/shared/form/FieldSearch';
import { SearchDocumentStore } from '@/stores/local/sidebar/SearchDocumentStore';
import { CreateFolderPopupStore } from '@/stores/local/sidebar/SidebarDocumentsStore/PopupsStore/CreateFolderPopupStore';
import { UploadDocumentPopupStore } from '@/stores/local/sidebar/SidebarDocumentsStore/PopupsStore/UploadDocumentPopupStore';
import { useDevices } from '@/utils/devices';

import s from './SearchTreeField.module.scss';

type SearchTreeFieldProps = {
  className?: string;
  store: SearchDocumentStore;
  documentPopup: UploadDocumentPopupStore;
  folderPopup: CreateFolderPopupStore;
  withAdd?: boolean;
  /** Глобальное право создавать папки (и в т.ч. в корне дерева). */
  canCreateFolder?: boolean;
  /** Загрузка документа из шапки без выбранной папки (личное пространство / сценарий бэка). */
  canUploadDocument?: boolean;
  disabledTooltip?: string;
};

const SearchTreeField: React.FC<SearchTreeFieldProps> = ({
  className,
  store,
  documentPopup,
  folderPopup,
  withAdd = true,
  canCreateFolder = true,
  canUploadDocument = true,
  disabledTooltip = 'Недостаточно прав для создания документов и папок',
}) => {
  const { isCompactViewport } = useDevices();

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (store.canSubmitSearch) {
          store.fetchDocumentsSearch();
        }
      }
    },
    [store],
  );

  return (
    <Flex gap="sm" className={cn(s.root, className)}>
      <FieldSearch
        onKeyDown={handleKeyDown}
        placeholder="Что вас интересует"
        size="md"
        store={store.searchField}
        onClear={store.reset}
        onSearch={store.fetchDocumentsSearch}
        isSearchDisabled={!store.canSubmitSearch}
        className={s.root__input}
      />
      {withAdd &&
        !isCompactViewport &&
        (canCreateFolder || canUploadDocument ? (
          <Menu radius={16} position="bottom-end" withinPortal={true} shadow="md">
            <Menu.Target>
              <ActionIcon variant="filled" size="40" className={s.root__button}>
                <IconPlus />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <>
                <Menu.Item onClick={documentPopup.openWithNull} disabled={!canUploadDocument}>
                  <Flex className={s['root__dropdown-item']} align="center" gap="xs">
                    <IconFileDescription size={16} />
                    <Text size="sm">Документы</Text>
                  </Flex>
                </Menu.Item>
                <Menu.Item onClick={folderPopup.openWithNull} disabled={!canCreateFolder}>
                  <Flex className={s['root__dropdown-item']} align="center" gap="xs">
                    <IconFolderPlus size={16} />
                    <Text size="sm">Папка</Text>
                  </Flex>
                </Menu.Item>
              </>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Tooltip label={disabledTooltip} withArrow position="bottom-end">
            <span>
              <ActionIcon variant="filled" size="40" className={s.root__button} disabled>
                <IconPlus />
              </ActionIcon>
            </span>
          </Tooltip>
        ))}
    </Flex>
  );
};

export default observer(SearchTreeField);
